import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Form, Col, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { parseISO, isValid as isValidDate } from 'date-fns';

import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMAAssinatura, CadernoLPMACultivo } from '../../models/modelsCadernoLPMA';
import api from '../../services/api';
import AssinaturaComNomesAdicionais, {
	parseNomesEnvolvidosJson,
	stringifyNomesEnvolvidos,
} from './components/AssinaturaComNomesAdicionais';
import MetodoLimpezaSelect from './components/MetodoLimpezaSelect';
import {
	formatarMetodoLimpezaParaJson,
	normalizarMetodoLimpezaParaFormulario,
} from '../../util/cadernoLPMA/metodoLimpeza';

interface CultivoModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMACultivo) => void;
	initialData?: CadernoLPMACultivo;
	isSoja?: boolean;
	isMilho?: boolean;
	assinaturasDisponiveis?: CadernoLPMAAssinatura[];
}

type FormValues = CadernoLPMACultivo & Record<string, any>;

type AbaCultivo = 1 | 2 | 3 | 4;

const defaultValues: Partial<FormValues> = {
	dataInicioCultivo: null,
	dataTerminoCultivo: null,
	talhaoCultivo: '',
	experimentoCultivo: '',
	responsavelCultivo: '',
	placaBiosseguranca: false,
	areaDelimitada: false,
	sojaBordadura: false,
	algodaoBordadura: false,
	sojaIsolamento: false,
	algodaoIsolamento: false,
	inspecaoRotina: false,
	cercasCultivo: false,
	montagemIrrigacao: false,
	observacoesMontagemIrrigacao: '',
	irrigacaoPosPlantio: false,
	avaliacoesCultivo: false,
	observacoesAvaliacoesCultivo: '',
	coleta: false,
	observacoesColeta: '',
	capinaRocagem: false,
	aplicacaoPulverizacao: false,
	aplicacaoPulverizacaoFungicida: false,
	aplicacaoPulverizacaoHerbicida: false,
	aplicacaoPulverizacaoInseticida: false,
	aplicacaoPulverizacaoOutros: false,
	aplicacaoPulverizacaoOutrosDescricao: '',
	adubacao: false,
	adubacaoCobertura: false,
	adubacaoFoliar: false,
	adubacaoOutros: false,
	adubacaoOutrosDescricao: '',
	destruicaoBordadura: false,
	observacoesDestruicaoBordadura: '',
	colheitaDestruicao: false,
	observacoesColheitaDestruicao: '',
	outrasAtividades: false,
	observacoesOutrasAtividades: '',
	observacoesCultivo: '',
};

const checkboxFields: Array<keyof FormValues> = [
	'placaBiosseguranca',
	'areaDelimitada',
	'sojaBordadura',
	'algodaoBordadura',
	'sojaIsolamento',
	'algodaoIsolamento',
	'inspecaoRotina',
	'cercasCultivo',
	'montagemIrrigacao',
	'irrigacaoPosPlantio',
	'avaliacoesCultivo',
	'coleta',
	'capinaRocagem',
	'aplicacaoPulverizacao',
	'aplicacaoPulverizacaoFungicida',
	'aplicacaoPulverizacaoHerbicida',
	'aplicacaoPulverizacaoInseticida',
	'aplicacaoPulverizacaoOutros',
	'adubacao',
	'adubacaoCobertura',
	'adubacaoFoliar',
	'adubacaoOutros',
	'destruicaoBordadura',
	'colheitaDestruicao',
	'outrasAtividades',
];

type ChecklistItem = {
	name: keyof FormValues;
	label: string;
	fieldName?: keyof FormValues;
	placeholder?: string;
	rows?: number;
};

const parseDateValue = (valor: any): Date | null => {
	if (!valor && valor !== 0) {
		return null;
	}

	if (valor instanceof Date) {
		return isValidDate(valor) ? valor : null;
	}

	if (typeof valor === 'string') {
		const texto = valor.trim();
		if (!texto) {
			return null;
		}

		const parsedIso = parseISO(texto);
		if (isValidDate(parsedIso)) {
			return parsedIso;
		}

		const parsedFallback = new Date(texto);
		return Number.isNaN(parsedFallback.getTime()) ? null : parsedFallback;
	}

	if (typeof valor === 'number') {
		const data = new Date(valor);
		return Number.isNaN(data.getTime()) ? null : data;
	}

	return null;
};

const parseJsonArray = (valor: any): Array<Record<string, any>> => {
	if (!valor) {
		return [];
	}

	if (Array.isArray(valor)) {
		return valor as Array<Record<string, any>>;
	}

	if (typeof valor === 'string') {
		try {
			const parsed = JSON.parse(valor);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}

	if (typeof valor === 'object') {
		return [valor as Record<string, any>];
	}

	return [];
};

export default function CultivoModal({
	show,
	onHide,
	onSubmit,
	initialData,
	isSoja,
	isMilho,
	assinaturasDisponiveis = [],
}: CultivoModalProps) {
	const [abaCultivo, setAbaCultivo] = useState<AbaCultivo>(1);
	const [quantidadeLinhasLimpeza, setQuantidadeLinhasLimpeza] = useState(1);
	
	// Estados para assinatura
	const [assinaturaEnvolvidos, setAssinaturaEnvolvidos] = useState<string | null>(null);
	const [assinaturaUsuario, setAssinaturaUsuario] = useState<string | null>(null);
	const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
	const [nomesAdicionais, setNomesAdicionais] = useState<string[]>([]);

	// Carregar assinatura do usuário
	useEffect(() => {
		const carregarAssinaturaUsuario = async () => {
			try {
				const token = localStorage.getItem('token');
				if (token) {
					const decoded: any = JSON.parse(atob(token.split('.')[1]));
					const username = decoded.unique_name || '';
					
					const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
					if (data && data.assinaturaBase64) {
						setAssinaturaUsuario(data.assinaturaBase64);
					}
				}
			} catch (error) {
				console.error('Erro ao carregar assinatura:', error);
			}
		};

		if (show) {
			carregarAssinaturaUsuario();
			
			// Carregar assinatura salva se existir
			if (initialData?.assinaturaEnvolvidosBase64) {
				setAssinaturaEnvolvidos(initialData.assinaturaEnvolvidosBase64);
			} else {
				// Limpar se não houver assinatura salva
				setAssinaturaEnvolvidos(null);
			}
			setNomesAdicionais(parseNomesEnvolvidosJson(initialData?.nomesEnvolvidosAdicionaisJson));
		} else {
			// Limpar estados ao fechar
			setAssinaturaEnvolvidos(null);
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, initialData?.assinaturaEnvolvidosBase64, initialData?.nomesEnvolvidosAdicionaisJson]);
	
	const {
		register,
		handleSubmit,
		control,
		reset,
		getValues,
		watch,
	} = useForm<FormValues>({
		mode: 'all',
		defaultValues: defaultValues,
		shouldUnregister: false,
	});
	const montagemIrrigacaoSelecionada = watch('montagemIrrigacao');

	const transformarCultivoParaFormulario = (dados?: CadernoLPMACultivo | null) => {
		const valores: Record<string, any> = {};

		if (dados) {
			Object.entries(dados).forEach(([chave, valor]) => {
				if (valor !== undefined) {
					valores[chave] = valor;
				}
			});
		}

		// Converte datas de string para Date ou null
		Object.keys(valores).forEach((chave) => {
			if (chave.toLowerCase().includes('data')) {
				valores[chave] = parseDateValue(dados ? (dados as any)[chave] : undefined);
			}
		});

		// Processar dados de limpeza de maquinários
		const limpeza = parseJsonArray(dados?.limpezaMaquinariosCultivoJson);
		limpeza.forEach((linha, indice) => {
			const metodoLimpeza = normalizarMetodoLimpezaParaFormulario(linha?.metodo);
			valores[`limpezaCultivoNome${indice}`] = linha?.nome ?? '';
			valores[`limpezaCultivoDataEntrada${indice}`] = parseDateValue(linha?.dataEntrada);
			valores[`limpezaCultivoDataSaida${indice}`] = parseDateValue(linha?.dataSaida);
			valores[`limpezaCultivoMetodo${indice}`] = metodoLimpeza.metodo;
			valores[`limpezaCultivoMetodoOutros${indice}`] = metodoLimpeza.outros;
			valores[`limpezaCultivoResponsavel${indice}`] = linha?.responsavel ?? '';
		});

		return {
			valores,
			quantidadeLinhasLimpeza: Math.max(limpeza.length, 1),
		};
	};

	// Estado para armazenar valores transformados
	const [valoresTransformados, setValoresTransformados] = useState<Record<string, any> | null>(null);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setAbaCultivo(1);
			setQuantidadeLinhasLimpeza(1);
			setValoresTransformados(null);
			return;
		}

		if (!initialData) {
			reset(defaultValues);
			setAbaCultivo(1);
			setQuantidadeLinhasLimpeza(1);
			setValoresTransformados(null);
			return;
		}

		const {
			valores,
			quantidadeLinhasLimpeza,
		} = transformarCultivoParaFormulario(initialData);

		setQuantidadeLinhasLimpeza(quantidadeLinhasLimpeza);
		setValoresTransformados(valores);
	}, [show, initialData]);

	// Resetar valores após quantidadeLinhasLimpeza ser atualizada
	useEffect(() => {
		if (valoresTransformados && quantidadeLinhasLimpeza > 0) {
			reset(valoresTransformados as FormValues);
		}
	}, [quantidadeLinhasLimpeza, valoresTransformados, reset]);

	const verificacoesBase = useMemo<ChecklistItem[]>(() => {
		const itens: ChecklistItem[] = [
			{
				name: 'placaBiosseguranca',
				label: 'Placa de identificação/biossegurança do ensaio presente no local, em bom estado de conservação e com informações legíveis e atualizadas.',
			},
			{
				name: 'areaDelimitada',
				label: 'Área experimental delimitada (estacas, bandeiras, cercas etc.).',
			},
			{
				name: 'inspecaoRotina',
				label: 'Inspeção de rotina para observações de ocorrências anormais na área experimental (presença de animais, fitotoxidade, mortandade de plantas, erosão, liberação não intencional etc.).',
			},
			{
				name: 'cercasCultivo',
				label: 'Cercas e portões de acesso em bom estado de conservação.',
			},
		];

		return itens;
	}, []);

	const verificacoesEspecificas = useMemo<ChecklistItem[]>(() => {
		const itens: ChecklistItem[] = [];

		if (isSoja) {
			itens.push(
				{
					name: 'sojaBordadura',
					label: 'Soja bordadura íntegra e contínua com no mínimo 5 metros.',
				},
				{
					name: 'algodaoBordadura',
					label: 'Algodão bordadura íntegra com no mínimo 25 metros (em caso de 250 metros de isolamento).',
				},
				{
					name: 'sojaIsolamento',
					label: 'Isolamento espacial Soja GM = distância mínima de 10 metros de qualquer cultivo/ensaio de soja comercial.',
				},
				{
					name: 'algodaoIsolamento',
					label: 'Isolamento espacial Algodão GM = distância mínima de 800 metros ou 250 metros com no mínimo 25 metros de bordadura de algodão convencional ou GM comercial de qualquer espécie de algodoeiro inclusive o cultivado.',
				},
			);
		}
		if (isMilho) {
			itens.push(
				{ name: 'sojaBordadura', label: 'Milho – Isolamento e bordadura conforme formulário “Definição de Isolamento e bordadura”.' },
			);
		}
		return itens;
	}, [isSoja, isMilho]);

	const limparBooleans = (values: FormValues): CadernoLPMACultivo => {
		const normalized = { ...values } as CadernoLPMACultivo;
		checkboxFields.forEach((field) => {
			// @ts-ignore – we know these are boolean friendly
			normalized[field] = !!values[field];
		});
		return normalized;
	};

	// Helper function para acessar propriedades dinâmicas sem erros de type
	const getNestedValue = (obj: any, key: string): any => obj?.[key];

	const renderAtividadeComCampo = (
		checkboxName: keyof FormValues,
		checkboxLabel: string,
		fieldName: keyof FormValues,
		placeholder: string,
		options?: { rows?: number },
	) => (
		<Form.Row className="row-custom">
			<Form.Group as={Col} md={12} className="mb-3">
				<Form.Check type="checkbox" ref={register} name={checkboxName} label={checkboxLabel} />
				<Form.Control
					as="textarea"
					rows={options?.rows ?? 2}
					ref={register}
					name={fieldName as string}
					className="mt-2"
					placeholder={placeholder}
				/>
			</Form.Group>
		</Form.Row>
	);

	const salvar = (dados: FormValues) => {
		const valores = getValues();
		const dadosCompletos = { ...dados, ...valores };
		
		// CONSOLIDAR DADOS DE LIMPEZA EM JSON
		const limpezaArray = [];
		for (let i = 0; i < quantidadeLinhasLimpeza; i++) {
			const nome = getNestedValue(dadosCompletos, `limpezaCultivoNome${i}`);
			const dataEntrada = getNestedValue(dadosCompletos, `limpezaCultivoDataEntrada${i}`);
			const dataSaida = getNestedValue(dadosCompletos, `limpezaCultivoDataSaida${i}`);
			const metodo = formatarMetodoLimpezaParaJson(
				getNestedValue(dadosCompletos, `limpezaCultivoMetodo${i}`),
				getNestedValue(dadosCompletos, `limpezaCultivoMetodoOutros${i}`),
			);
			const responsavel = getNestedValue(dadosCompletos, `limpezaCultivoResponsavel${i}`);

			// Apenas incluir no array se houver pelo menos um campo preenchido
			if (nome || dataEntrada || dataSaida || metodo || responsavel) {
				limpezaArray.push({
					nome,
					dataEntrada,
					dataSaida,
					metodo,
					responsavel
				});
			}

			delete dadosCompletos[`limpezaCultivoMetodo${i}`];
			delete dadosCompletos[`limpezaCultivoMetodoOutros${i}`];
		}

		// Converter limpeza para JSON apenas se houver dados
		if (limpezaArray.length > 0) {
			dadosCompletos.limpezaMaquinariosCultivoJson = JSON.stringify(limpezaArray);
		} else {
			dadosCompletos.limpezaMaquinariosCultivoJson = null;
		}

		if (!dadosCompletos.montagemIrrigacao) {
			dadosCompletos.observacoesMontagemIrrigacao = null;
		}
		
		const dadosComAssinatura = {
			...limparBooleans(dadosCompletos),
			assinaturaEnvolvidosBase64: assinaturaEnvolvidos || null,
			nomesEnvolvidosAdicionaisJson: stringifyNomesEnvolvidos(nomesAdicionais),
		};
		
		onSubmit(dadosComAssinatura);
		onHide();
	};

	const abasCultivo: Array<{ valor: AbaCultivo; label: string }> = [
		{ valor: 1, label: 'Dados Gerais' },
		{ valor: 2, label: 'Verificações' },
		{ valor: 3, label: 'Atividades e Observações' },
		{ valor: 4, label: 'Limpeza de Maquinários' },
	];

	return (
		<Modal show={show} onHide={onHide} title="Formulário – Atividades de Cultivo/Manejo" bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(salvar)} autoComplete="off">
				{/* Navegação de Abas */}
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<div className="button-position-caderno" style={{ justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
							{abasCultivo.map((aba) => (
								<Button
									key={aba.valor}
									title={aba.label}
									contained={abaCultivo === aba.valor}
									className={abaCultivo === aba.valor ? 'active' : ''}
									type="button"
									onClick={() => setAbaCultivo(aba.valor)}
								/>
							))}
						</div>
					</Form.Group>
				</Form.Row>

				<hr className="my-3" />

				{/* ABA 1 - DADOS GERAIS */}
				{abaCultivo === 1 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label><h2>Formulário – Atividades de Cultivo/Manejo</h2></Form.Label>
								<p className="text-muted">Preencha por ocasião das atividades de cultivo.</p>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={3}>
								<Form.Label>Talhão</Form.Label>
								<Form.Control ref={register} name="talhaoCultivo" placeholder="Identifique o talhão" />
							</Form.Group>
							<Form.Group as={Col} md={3}>
								<Form.Label>Experimento</Form.Label>
								<Form.Control ref={register} name="experimentoCultivo" placeholder="Informe o experimento" />
							</Form.Group>
							<Form.Group as={Col} md={3}>
								<Form.Label>Data inicial</Form.Label>
								<Controller
									control={control}
									name="dataInicioCultivo"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value || null}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
							<Form.Group as={Col} md={3}>
								<Form.Label>Data final</Form.Label>
								<Controller
									control={control}
									name="dataTerminoCultivo"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value || null}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
						</Form.Row>

						<AssinaturaComNomesAdicionais
							label="assinatura"
							assinaturaUsuario={assinaturaUsuario}
							assinaturaBase64={assinaturaEnvolvidos}
							onAssinaturaBase64Change={setAssinaturaEnvolvidos}
							nomesAdicionais={nomesAdicionais}
							onNomesAdicionaisChange={setNomesAdicionais}
							mostrarAssinatura={mostrarAssinatura}
							onMostrarAssinaturaChange={setMostrarAssinatura}
							assinaturasDisponiveis={assinaturasDisponiveis}
						/>
					</>
				)}

				{/* ABA 2 - VERIFICAÇÕES */}
				{abaCultivo === 2 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label><h2>Registro de verificações</h2></Form.Label>
							</Form.Group>
						</Form.Row>

						{verificacoesBase.concat(verificacoesEspecificas).map(({ name, label }) => (
							<Form.Row className="row-custom" key={name}>
								<Form.Group as={Col} md={12}>
									<Form.Check type="checkbox" ref={register} name={name} label={label} />
								</Form.Group>
							</Form.Row>
						))}
					</>
				)}

				{/* ABA 3 - ATIVIDADES E OBSERVAÇÕES */}
				{abaCultivo === 3 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label><h2>Registro de atividades</h2></Form.Label>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12} className="mb-3">
								<Form.Label className="font-weight-bold">Montagem da irrigação / Irrigação após o plantio</Form.Label>
								<div className="d-flex flex-column flex-md-row flex-wrap align-items-start" style={{ gap: '16px' }}>
									<Form.Check type="checkbox" ref={register} name="montagemIrrigacao" label="Montagem da irrigação" />
									<Form.Check
										type="checkbox"
										ref={register}
										name="irrigacaoPosPlantio"
										label="Irrigação após o plantio – lâmina aplicada (mm)"
									/>
								</div>
								{montagemIrrigacaoSelecionada && (
									<Form.Control
										as="textarea"
										rows={2}
										className="mt-2"
										ref={register}
										name="observacoesMontagemIrrigacao"
										placeholder="Descreva detalhes da montagem da irrigação"
									/>
								)}
								<small className="text-muted">Registre a lâmina aplicada no campo de observações gerais.</small>
							</Form.Group>
						</Form.Row>

						{renderAtividadeComCampo(
							'avaliacoesCultivo',
							'* Avaliações (*especificar)',
							'observacoesAvaliacoesCultivo',
							'Detalhe as avaliações realizadas',
						)}

						{renderAtividadeComCampo(
							'coleta',
							'* Coleta/envio de amostras/material GM (*registrar no formulário específico neste caderno de campo)',
							'observacoesColeta',
							'Descreva detalhes das coletas/envios',
						)}

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12} className="mb-3">
								<Form.Check type="checkbox" ref={register} name="capinaRocagem" label="Capina ou roçagem executada." />
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12} className="mb-3">
								<Form.Label className="font-weight-bold">Aplicação/Pulverização</Form.Label>
								<div className="d-flex flex-column flex-md-row flex-wrap align-items-start" style={{ gap: '16px' }}>
									<Form.Check type="checkbox" ref={register} name="aplicacaoPulverizacaoHerbicida" label="Herbicida" />
									<Form.Check type="checkbox" ref={register} name="aplicacaoPulverizacaoInseticida" label="Inseticida" />
									<Form.Check type="checkbox" ref={register} name="aplicacaoPulverizacaoFungicida" label="Fungicida" />
									<Form.Check type="checkbox" ref={register} name="aplicacaoPulverizacao" label="Protocolo" />
									<Form.Check type="checkbox" ref={register} name="aplicacaoPulverizacaoOutros" label="Outros (especificar)" />
								</div>
								<Form.Control
									as="textarea"
									rows={2}
									className="mt-2"
									ref={register}
									name="aplicacaoPulverizacaoOutrosDescricao"
									placeholder="Protocolo, produto ou observações adicionais"
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12} className="mb-3">
								<Form.Label className="font-weight-bold">Adubação</Form.Label>
								<div className="d-flex flex-column flex-md-row flex-wrap align-items-start" style={{ gap: '16px' }}>
									<Form.Check type="checkbox" ref={register} name="adubacao" label="Aplicada" />
									<Form.Check type="checkbox" ref={register} name="adubacaoCobertura" label="Cobertura" />
									<Form.Check type="checkbox" ref={register} name="adubacaoFoliar" label="Foliar" />
									<Form.Check type="checkbox" ref={register} name="adubacaoOutros" label="Outras (especificar)" />
								</div>
								<Form.Control
									as="textarea"
									rows={2}
									className="mt-2"
									ref={register}
									name="adubacaoOutrosDescricao"
									placeholder="Detalhe outras adubações realizadas"
								/>
							</Form.Group>
						</Form.Row>

						{renderAtividadeComCampo(
							'destruicaoBordadura',
							'* Destruição da bordadura (*registrar no formulário específico neste caderno de campo)',
							'observacoesDestruicaoBordadura',
							'Detalhes sobre destruição da bordadura',
						)}

						{renderAtividadeComCampo(
							'colheitaDestruicao',
							'* Colheita/Destruição (*registrar nos formulários específicos neste caderno de campo)',
							'observacoesColheitaDestruicao',
							'Detalhes da colheita ou destruição',
						)}

						{renderAtividadeComCampo(
							'outrasAtividades',
							'* Outras atividades – descreva abaixo no campo de observações e informações adicionais.',
							'observacoesOutrasAtividades',
							'Descreva outras atividades executadas',
						)}

						<hr className="my-4" />

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label><h2>Observações e informações adicionais</h2></Form.Label>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label>Observações e informações adicionais</Form.Label>
								<Form.Control
									ref={register}
									name="observacoesCultivo"
									as="textarea"
									rows={3}
									placeholder="Informe observações gerais do cultivo"
								/>
							</Form.Group>
						</Form.Row>
					</>
				)}

				{/* ABA 4 - LIMPEZA DE MAQUINÁRIOS */}
				{abaCultivo === 4 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label><h2>Formulário de limpeza de maquinários/equipamentos/veículos - utilizados nas atividades de cultivo</h2></Form.Label>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<div className="table-responsive">
									<table className="table table-bordered table-sm">
										<thead className="table-light">
											<tr>
												<th>Nome e código do maquinário, equipamento e/ou veículo utilizado</th>
												<th colSpan={2}>Datas de limpeza</th>
												<th>Método de limpeza</th>
												<th>Responsável</th>
											</tr>
											<tr>
												<th></th>
												<th>Antes ENTRADA</th>
												<th>Antes SAÍDA</th>
												<th></th>
												<th></th>
											</tr>
										</thead>
										<tbody>
											{[...Array(quantidadeLinhasLimpeza)].map((_, index) => {
												const metodoSelecionado = watch(`limpezaCultivoMetodo${index}` as any);
												return (
												<tr key={index}>
													<td>
														<Form.Control
															type="text"
															size="sm"
															placeholder="Nome e código"
															className="mb-2"
															ref={register}
															name={`limpezaCultivoNome${index}`}
														/>
													</td>
													<td>
														<Controller
															control={control}
															name={`limpezaCultivoDataEntrada${index}` as any}
															render={({ onChange, value }) => (
																<DatePicker
																	selected={parseDateValue(value)}
																	onChange={onChange}
																	className="input-custom-to-date"
																	locale="pt-BR"
																	dateFormat="dd/MM/yyyy"
																	placeholderText="/ /"
																	inline={false}
																/>
															)}
														/>
													</td>
													<td>
														<Controller
															control={control}
															name={`limpezaCultivoDataSaida${index}` as any}
															render={({ onChange, value }) => (
																<DatePicker
																	selected={parseDateValue(value)}
																	onChange={onChange}
																	className="input-custom-to-date"
																	locale="pt-BR"
																	dateFormat="dd/MM/yyyy"
																	placeholderText="/ /"
																	inline={false}
																/>
															)}
														/>
													</td>
													<td>
														<MetodoLimpezaSelect
															register={register}
															name={`limpezaCultivoMetodo${index}`}
															nameOutros={`limpezaCultivoMetodoOutros${index}`}
															metodoSelecionado={metodoSelecionado}
															className="mb-2"
														/>
													</td>
													<td>
														<Form.Control
															type="text"
															size="sm"
															placeholder="Responsável"
															className="mb-2"
															ref={register}
															name={`limpezaCultivoResponsavel${index}`}
														/>
													</td>
												</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Button
									title="Adicionar Equipamento"
									contained={true}
									type="button"
									onClick={() => setQuantidadeLinhasLimpeza(quantidadeLinhasLimpeza + 1)}
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label>Observações sobre limpeza de maquinários</Form.Label>
								<Form.Control
									as="textarea"
									rows={3}
									ref={register}
									name="observacaoLimpezaMaquinariosCultivo"
									placeholder="Comentários adicionais sobre a limpeza"
								/>
							</Form.Group>
						</Form.Row>
					</>
				)}

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Salvar" contained type="submit" />
				</div>
			</Form>
		</Modal>
	);
}
